import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, Cancel, Save } from '@mui/icons-material';
import { RefundView } from '../../../../../services/RefundService/types/refundTypes';
import { useDocumentUpload } from './hooks';
import {
  DropZoneSection,
  SelectedFilesSection,
  UploadProgressSection,
  UploadGuidelinesSection,
} from './components';
import { ALLOWED_EXTENSIONS } from './constants';

export interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  refund: RefundView;
}

export const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  open,
  onClose,
  refund,
}) => {
  const {
    selectedFiles,
    isUploading,
    uploadProgress,
    error,
    isDragOver,
    fileInputRef,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUpload,
    handleClose,
    handleBrowseFiles,
  } = useDocumentUpload({ refund, onClose });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CloudUpload sx={{ color: '#3b82f6', fontSize: 28 }} />
        Upload Documents
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <DropZoneSection
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseFiles}
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={ALLOWED_EXTENSIONS.join(',')}
          />

          <SelectedFilesSection
            selectedFiles={selectedFiles}
            isUploading={isUploading}
            onRemoveFile={handleRemoveFile}
          />

          <UploadProgressSection isUploading={isUploading} uploadProgress={uploadProgress} />

          <UploadGuidelinesSection />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={isUploading}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <Save />}
          disabled={selectedFiles.length === 0 || isUploading}
          sx={{
            backgroundColor: '#162346',
            '&:hover': {
              backgroundColor: '#1a2a52',
            },
            '&:disabled': {
              backgroundColor: '#e5e7eb',
              color: '#9ca3af',
            },
          }}
        >
          {isUploading
            ? 'Uploading...'
            : `Upload ${selectedFiles.length} Document${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentDialog;
